import { authenticatedFetch } from '../utils/authenticatedFetch';
import { API_BASE_URL } from '../config/apiConfig';

export interface PayslipSettings {
  id?: number;
  dataType: 'salary' | 'bonus' | 'construction';
  borderColor: string;
  backgroundColor: string;
  roumuhiBorderColor?: string;
  roumuhiBackgroundColor?: string;
  fontSize: string;
  fontFamily: string;
  showCompanyName: boolean;
  showEmployeeCode: boolean;
  showMemo: boolean;
  payment: FieldSettings;
  deduction: FieldSettings;
  attendance: FieldSettings;
  notes: FieldSettings;
  furikomi?: FieldSettings; // Only for construction
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface FieldSettings {
  enabled: string[];
  lastUpdated: string;
  updatedBy: string;
}

export interface PayslipSettingsResponse {
  success: boolean;
  settings?: PayslipSettings;
  message?: string;
}

/**
 * Service for managing payslip settings via backend API
 * Replaces localStorage-based settings with centralized database storage
 */
class PayslipSettingsServiceClass {
  private baseUrl = `${API_BASE_URL}/payslip-settings`;

  /**
   * Get settings for a specific data type (salary, bonus, construction)
   */
  async getSettings(dataType: 'salary' | 'bonus' | 'construction'): Promise<PayslipSettings | null> {
    try {
      const response = await authenticatedFetch(`${this.baseUrl}/${dataType}`);
      
      if (!response.ok) {
        console.error(`Failed to fetch settings for ${dataType}:`, response.statusText);
        return null;
      }

      const result: PayslipSettingsResponse = await response.json();
      
      if (result.success && result.settings) {
        return result.settings;
      } else {
        console.error(`Failed to get settings for ${dataType}:`, result.message);
        return null;
      }
    } catch (error) {
      console.error(`Error fetching settings for ${dataType}:`, error);
      return null;
    }
  }

  /**
   * Save settings for a specific data type
   */
  async saveSettings(
    dataType: 'salary' | 'bonus' | 'construction', 
    settings: PayslipSettings
  ): Promise<boolean> {
    try {
      // Ensure dataType matches the URL parameter
      const settingsToSave = { ...settings, dataType };

      const response = await authenticatedFetch(`${this.baseUrl}/${dataType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settingsToSave),
      });

      if (!response.ok) {
        console.error(`Failed to save settings for ${dataType}:`, response.statusText);
        return false;
      }

      const result: PayslipSettingsResponse = await response.json();
      
      if (result.success) {
        console.log(`Settings saved successfully for ${dataType}`);
        return true;
      } else {
        console.error(`Failed to save settings for ${dataType}:`, result.message);
        return false;
      }
    } catch (error) {
      console.error(`Error saving settings for ${dataType}:`, error);
      return false;
    }
  }

  /**
   * Update settings for a specific data type
   */
  async updateSettings(
    dataType: 'salary' | 'bonus' | 'construction', 
    settings: Partial<PayslipSettings>
  ): Promise<boolean> {
    try {
      const response = await authenticatedFetch(`${this.baseUrl}/${dataType}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        console.error(`Failed to update settings for ${dataType}:`, response.statusText);
        return false;
      }

      const result: PayslipSettingsResponse = await response.json();
      
      if (result.success) {
        console.log(`Settings updated successfully for ${dataType}`);
        return true;
      } else {
        console.error(`Failed to update settings for ${dataType}:`, result.message);
        return false;
      }
    } catch (error) {
      console.error(`Error updating settings for ${dataType}:`, error);
      return false;
    }
  }

  /**
   * Update specific field settings (payment, deduction, attendance, notes, furikomi)
   */
  async updateFieldSettings(
    dataType: 'salary' | 'bonus' | 'construction',
    fieldType: 'payment' | 'deduction' | 'attendance' | 'notes' | 'furikomi',
    fieldSettings: FieldSettings
  ): Promise<boolean> {
    try {
      const response = await authenticatedFetch(`${this.baseUrl}/${dataType}/fields/${fieldType}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(fieldSettings),
      });

      if (!response.ok) {
        console.error(`Failed to update ${fieldType} settings for ${dataType}:`, response.statusText);
        return false;
      }

      const result: PayslipSettingsResponse = await response.json();
      
      if (result.success) {
        console.log(`${fieldType} settings updated successfully for ${dataType}`);
        return true;
      } else {
        console.error(`Failed to update ${fieldType} settings for ${dataType}:`, result.message);
        return false;
      }
    } catch (error) {
      console.error(`Error updating ${fieldType} settings for ${dataType}:`, error);
      return false;
    }
  }

  /**
   * Get settings with fallback to localStorage if API fails
   * This provides backward compatibility during migration
   */
  async getSettingsWithFallback(dataType: 'salary' | 'bonus' | 'construction'): Promise<PayslipSettings | null> {
    // Try to get from API first
    const apiSettings = await this.getSettings(dataType);
    if (apiSettings) {
      return apiSettings;
    }

    // Fallback to localStorage if API fails
    console.warn(`API failed for ${dataType} settings, falling back to localStorage`);
    return this.getSettingsFromLocalStorage(dataType);
  }

  /**
   * Save settings with fallback to localStorage if API fails
   */
  async saveSettingsWithFallback(
    dataType: 'salary' | 'bonus' | 'construction', 
    settings: PayslipSettings
  ): Promise<boolean> {
    // Try to save to API first
    const apiSuccess = await this.saveSettings(dataType, settings);
    if (apiSuccess) {
      return true;
    }

    // Fallback to localStorage if API fails
    console.warn(`API failed for ${dataType} settings, falling back to localStorage`);
    this.saveSettingsToLocalStorage(dataType, settings);
    return true;
  }

  /**
   * Fallback method to get settings from localStorage
   */
  private getSettingsFromLocalStorage(dataType: 'salary' | 'bonus' | 'construction'): PayslipSettings | null {
    try {
      const storageKey = `payslipSettings_${dataType}`;
      const savedSettings = localStorage.getItem(storageKey);
      
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        return {
          ...parsed,
          dataType,
        };
      }
    } catch (error) {
      console.error(`Error reading ${dataType} settings from localStorage:`, error);
    }
    
    return null;
  }

  /**
   * Fallback method to save settings to localStorage
   */
  private saveSettingsToLocalStorage(
    dataType: 'salary' | 'bonus' | 'construction', 
    settings: PayslipSettings
  ): void {
    try {
      const storageKey = `payslipSettings_${dataType}`;
      localStorage.setItem(storageKey, JSON.stringify(settings));
      console.log(`${dataType} settings saved to localStorage as fallback`);
    } catch (error) {
      console.error(`Error saving ${dataType} settings to localStorage:`, error);
    }
  }

  /**
   * Clear localStorage settings (useful for migration)
   */
  clearLocalStorageSettings(dataType?: 'salary' | 'bonus' | 'construction'): void {
    if (dataType) {
      const storageKey = `payslipSettings_${dataType}`;
      localStorage.removeItem(storageKey);
      console.log(`Cleared localStorage settings for ${dataType}`);
    } else {
      // Clear all payslip settings from localStorage
      const keys = ['payslipSettings_salary', 'payslipSettings_bonus', 'payslipSettings_construction'];
      keys.forEach(key => localStorage.removeItem(key));
      console.log('Cleared all payslip settings from localStorage');
    }
  }

  /**
   * Migrate settings from localStorage to API
   */
  async migrateSettingsFromLocalStorage(dataType: 'salary' | 'bonus' | 'construction'): Promise<boolean> {
    const localSettings = this.getSettingsFromLocalStorage(dataType);
    
    if (localSettings) {
      console.log(`Migrating ${dataType} settings from localStorage to API`);
      const success = await this.saveSettings(dataType, localSettings);
      
      if (success) {
        // Clear localStorage after successful migration
        this.clearLocalStorageSettings(dataType);
        console.log(`Successfully migrated ${dataType} settings to API`);
        return true;
      }
    }
    
    return false;
  }
}

// Export singleton instance
export const PayslipSettingsService = new PayslipSettingsServiceClass();
